import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080';
const ABSTRACT_METADATA_API = `${CONFERENCE_API}/api/abstract-metadata`;

const AbstractMetadataManagement = () => {
  const [activeConferences, setActiveConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [abstractMetadata, setAbstractMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingMetadata, setViewingMetadata] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [alert, setAlert] = useState(null);
  const [editingMetadata, setEditingMetadata] = useState(null);

  const [formData, setFormData] = useState({
    conferenceId: '',
    submissionDeadline: '',
    coAuthors: false,
    approvalMandate: false,
    reviewDeadline: '',
    acceptedDocTypes: 'PDF_ONLY',
    mode: 'ORAL',
    maxAbstractChars: '',
    maxSubmissions: '',
    maxFileSizeMb: '',
    visibleToReviewers: false,
    emailRemainderSubmission: '',
    emailRemainderReviewer: '',
    abstractStepNumber: 0
  });

  const [errors, setErrors] = useState({});

  const docTypesOptions = ['PDF_ONLY', 'DOC_ONLY'];

  // Fetch active conferences
  const fetchActiveConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setActiveConferences(data);
      } else {
        showAlert('Failed to fetch conferences', 'error');
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
      showAlert('Error fetching conferences', 'error');
    }
  };

  // Fetch abstract metadata by conference
  const fetchAbstractMetadata = async (conferenceId) => {
    if (!conferenceId) {
      setAbstractMetadata([]);
      return;
    }

    console.log('Fetching abstract metadata for conference:', conferenceId);
    setLoading(true);
    try {
      const response = await fetch(`${ABSTRACT_METADATA_API}/conference/${conferenceId}`);
      console.log('Fetch response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched abstract metadata:', data);
        // API returns an array
        setAbstractMetadata(Array.isArray(data) ? data : []);
      } else if (response.status === 404) {
        // No metadata found for this conference - this is normal
        setAbstractMetadata([]);
      } else {
        showAlert('Failed to fetch abstract metadata', 'error');
        setAbstractMetadata([]);
      }
    } catch (error) {
      console.error('Error fetching abstract metadata:', error);
      showAlert('Error fetching abstract metadata', 'error');
      setAbstractMetadata([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveConferences();
  }, []);

  useEffect(() => {
    if (selectedConferenceId) {
      fetchAbstractMetadata(selectedConferenceId);
    } else {
      setAbstractMetadata([]);
    }
  }, [selectedConferenceId]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.conferenceId) {
      newErrors.conferenceId = 'Conference is required';
    }
    if (!formData.submissionDeadline) {
      newErrors.submissionDeadline = 'Submission deadline is required';
    }
    if (!formData.reviewDeadline) {
      newErrors.reviewDeadline = 'Review deadline is required';
    }
    if (formData.submissionDeadline && formData.reviewDeadline) {
      if (new Date(formData.reviewDeadline) < new Date(formData.submissionDeadline)) {
        newErrors.reviewDeadline = 'Review deadline must be after submission deadline';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOpenModal = (mode, metadata = null) => {
    setModalMode(mode);
    if (mode === 'edit' && metadata) {
      setEditingMetadata(metadata);
      setFormData({
        conferenceId: metadata.conferenceId,
        submissionDeadline: metadata.submissionDeadline,
        coAuthors: metadata.coAuthors,
        approvalMandate: metadata.approvalMandate || false,
        reviewDeadline: metadata.reviewDeadline,
        acceptedDocTypes: metadata.acceptedDocTypes,
        mode: metadata.mode,
        maxAbstractChars: metadata.maxAbstractChars || '',
        maxSubmissions: metadata.maxSubmissions || '',
        maxFileSizeMb: metadata.maxFileSizeMb || '',
        visibleToReviewers: metadata.visibleToReviewers || false,
        abstractStepNumber: metadata.abstractStepNumber || 0
      });
    } else {
      setEditingMetadata(null);
      setFormData({
        conferenceId: selectedConferenceId || '',
        submissionDeadline: '',
        coAuthors: false,
        approvalMandate: false,
        reviewDeadline: '',
        acceptedDocTypes: 'PDF_ONLY',
        mode: 'ORAL',
        maxAbstractChars: '',
        maxSubmissions: '',
        maxFileSizeMb: '',
        visibleToReviewers: false,
        abstractStepNumber: 0
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMetadata(null);
    setFormData({
      conferenceId: '',
      submissionDeadline: '',
      coAuthors: false,
      approvalMandate: false,
      reviewDeadline: '',
      acceptedDocTypes: 'PDF_ONLY',
      mode: 'ORAL',
      maxAbstractChars: '',
      maxSubmissions: '',
      maxFileSizeMb: '',
      visibleToReviewers: false,
      abstractStepNumber: 0
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const url = modalMode === 'create' 
        ? ABSTRACT_METADATA_API 
        : `${ABSTRACT_METADATA_API}/${editingMetadata.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Prepare payload - convert empty strings to null for numeric fields and trim text
      const payload = {
        conferenceId: Number(formData.conferenceId),
        submissionDeadline: formData.submissionDeadline?.trim(),
        coAuthors: formData.coAuthors,
        approvalMandate: formData.approvalMandate,
        reviewDeadline: formData.reviewDeadline?.trim(),
        acceptedDocTypes: formData.acceptedDocTypes?.trim(),
        mode: formData.mode?.trim(),
        maxAbstractChars: formData.maxAbstractChars === '' ? null : Number(formData.maxAbstractChars),
        maxSubmissions: formData.maxSubmissions === '' ? null : Number(formData.maxSubmissions),
        maxFileSizeMb: formData.maxFileSizeMb === '' ? null : Number(formData.maxFileSizeMb),
        visibleToReviewers: formData.visibleToReviewers,
        emailRemainderSubmission: formData.emailRemainderSubmission === '' ? null : Number(formData.emailRemainderSubmission),
        emailRemainderReviewer: formData.emailRemainderReviewer === '' ? null : Number(formData.emailRemainderReviewer),
        abstractStepNumber: Number(formData.abstractStepNumber)
      };

      console.log('Update request:', {
        url,
        method,
        payload,
        formData,
        editingMetadata
      });
      console.log('Payload JSON:', JSON.stringify(payload, null, 2));

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        let responseData;
        const contentType = response.headers.get('content-type');
        console.log('Response content-type:', contentType);
        
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
          console.log('Response data:', responseData);
        } else {
          const text = await response.text();
          console.log('Response text:', text);
        }
        
        showAlert(`Abstract metadata ${modalMode === 'create' ? 'created' : 'updated'} successfully!`, 'success');
        handleCloseModal();
        fetchAbstractMetadata(selectedConferenceId || formData.conferenceId);
      } else {
        const errorText = await response.text();
        console.error('Update error:', errorText);
        showAlert(errorText || `Failed to ${modalMode} abstract metadata`, 'error');
      }
    } catch (error) {
      console.error(`Error ${modalMode}ing abstract metadata:`, error);
      showAlert(`Error ${modalMode}ing abstract metadata`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (metadataId) => {
    if (!window.confirm('Are you sure you want to delete this abstract metadata?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${ABSTRACT_METADATA_API}/${metadataId}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 204) {
        showAlert('Abstract metadata deleted successfully!', 'success');
        fetchAbstractMetadata(selectedConferenceId);
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to delete abstract metadata', 'error');
      }
    } catch (error) {
      console.error('Error deleting abstract metadata:', error);
      showAlert('Error deleting abstract metadata', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getAbstractStepLabel = (stepNumber) => {
    switch (stepNumber) {
      case 0:
        return 'Just Conference';
      case 3:
        return 'Register And Pay';
      case 7:
        return 'Pay And Register';
      default:
        return stepNumber;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Abstract Metadata Management</h2>
        <button
          onClick={() => handleOpenModal('create')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
          disabled={!selectedConferenceId}
        >
          + Add Abstract Metadata
        </button>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Conference:
        </label>
        <select
          value={selectedConferenceId}
          onChange={(e) => setSelectedConferenceId(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">-- Select Conference --</option>
          {activeConferences.map(conf => (
            <option key={conf.id} value={conf.id}>
              {conf.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

      {!loading && selectedConferenceId && abstractMetadata.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No abstract metadata found for this conference. Click "Add Abstract Metadata" to create one.
        </div>
      )}

      {!loading && abstractMetadata.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Submission Deadline</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Review Deadline</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Doc Types</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Mode</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Approval Mandate</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {abstractMetadata.map(metadata => (
                <tr key={metadata.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{formatDate(metadata.submissionDeadline)}</td>
                  <td style={{ padding: '12px' }}>{formatDate(metadata.reviewDeadline)}</td>
                  <td style={{ padding: '12px' }}>{metadata.acceptedDocTypes}</td>
                  <td style={{ padding: '12px' }}>{metadata.mode}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{metadata.approvalMandate ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        setViewingMetadata(metadata);
                        setShowViewModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '12px'
                      }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleOpenModal('edit', metadata)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffc107',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(metadata.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              {modalMode === 'create' ? 'Create Abstract Metadata' : 'Edit Abstract Metadata'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Conference: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="conferenceId"
                  value={formData.conferenceId}
                  onChange={handleInputChange}
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.conferenceId ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select Conference --</option>
                  {activeConferences.map(conf => (
                    <option key={conf.id} value={conf.id}>
                      {conf.name}
                    </option>
                  ))}
                </select>
                {errors.conferenceId && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.conferenceId}</span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Submission Deadline: <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="submissionDeadline"
                    value={formData.submissionDeadline}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.submissionDeadline ? '1px solid red' : '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.submissionDeadline && (
                    <span style={{ color: 'red', fontSize: '12px' }}>{errors.submissionDeadline}</span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Review Deadline: <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="reviewDeadline"
                    value={formData.reviewDeadline}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.reviewDeadline ? '1px solid red' : '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.reviewDeadline && (
                    <span style={{ color: 'red', fontSize: '12px' }}>{errors.reviewDeadline}</span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Accepted Document Types: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="acceptedDocTypes"
                  value={formData.acceptedDocTypes}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  {docTypesOptions.map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Presentation Mode: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="mode"
                  value={formData.mode}
                  onChange={handleInputChange}
                  placeholder="e.g., ORAL, POSTER, VIRTUAL, HYBRID"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Enter presentation mode (e.g., ORAL, POSTER, VIRTUAL, HYBRID, etc.)
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Max Characters:
                  </label>
                  <input
                    type="number"
                    name="maxAbstractChars"
                    value={formData.maxAbstractChars}
                    onChange={handleInputChange}
                    placeholder="e.g., 5000"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Max Submissions:
                  </label>
                  <input
                    type="number"
                    name="maxSubmissions"
                    value={formData.maxSubmissions}
                    onChange={handleInputChange}
                    placeholder="e.g., 3"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Max File Size (MB):
                  </label>
                  <input
                    type="number"
                    name="maxFileSizeMb"
                    value={formData.maxFileSizeMb}
                    onChange={handleInputChange}
                    placeholder="e.g., 10"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Email Reminder - Submission (days before deadline):
                  </label>
                  <input
                    type="number"
                    name="emailRemainderSubmission"
                    value={formData.emailRemainderSubmission}
                    onChange={handleInputChange}
                    placeholder="e.g., 7"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Email Reminder - Reviewer (days before deadline):
                  </label>
                  <input
                    type="number"
                    name="emailRemainderReviewer"
                    value={formData.emailRemainderReviewer}
                    onChange={handleInputChange}
                    placeholder="e.g., 3"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Abstract Step Number: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="abstractStepNumber"
                  value={formData.abstractStepNumber}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value={0}>Just Conference</option>
                  <option value={3}>Register And Pay</option>
                  <option value={7}>Pay And Register</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="coAuthors"
                      checked={formData.coAuthors}
                      onChange={handleInputChange}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>Allow Co-Authors</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="approvalMandate"
                      checked={formData.approvalMandate}
                      onChange={handleInputChange}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>Approval Mandate</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="visibleToReviewers"
                      checked={formData.visibleToReviewers}
                      onChange={handleInputChange}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold' }}>Visible to Reviewers</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && viewingMetadata && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Abstract Metadata Details</h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Conference:</strong>
                <span>{viewingMetadata.conferenceName}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Submission Deadline:</strong>
                <span>{formatDate(viewingMetadata.submissionDeadline)}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Review Deadline:</strong>
                <span>{formatDate(viewingMetadata.reviewDeadline)}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Co-Authors:</strong>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  backgroundColor: viewingMetadata.coAuthors ? '#28a745' : '#6c757d',
                  color: 'white',
                  fontSize: '12px',
                  display: 'inline-block'
                }}>
                  {viewingMetadata.coAuthors ? 'Yes' : 'No'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Approval Mandate:</strong>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  backgroundColor: viewingMetadata.approvalMandate ? '#28a745' : '#6c757d',
                  color: 'white',
                  fontSize: '12px',
                  display: 'inline-block'
                }}>
                  {viewingMetadata.approvalMandate ? 'Yes' : 'No'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Document Types:</strong>
                <span>{viewingMetadata.acceptedDocTypes}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Presentation Mode:</strong>
                <span>{viewingMetadata.mode}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Max Abstract Chars:</strong>
                <span>{viewingMetadata.maxAbstractChars || '-'}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Max Submissions:</strong>
                <span>{viewingMetadata.maxSubmissions || '-'}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Max File Size (MB):</strong>
                <span>{viewingMetadata.maxFileSizeMb || '-'}</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Visible to Reviewers:</strong>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  backgroundColor: viewingMetadata.visibleToReviewers ? '#28a745' : '#6c757d',
                  color: 'white',
                  fontSize: '12px',
                  display: 'inline-block'
                }}>
                  {viewingMetadata.visibleToReviewers ? 'Yes' : 'No'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', marginBottom: '12px' }}>
                <strong>Abstract Step Number:</strong>
                <span>{getAbstractStepLabel(viewingMetadata.abstractStepNumber || 0)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingMetadata(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbstractMetadataManagement;
